import type { ClassMap } from '../models';

/**
 * Tunable constants for the cabin model. Every number the cabin maths relies
 * on lives here so balance changes never touch calculation code.
 */

/* -------------------------------------------------------------------------- *
 * Weight and fuel
 * -------------------------------------------------------------------------- */

/** Installed weight of one seat (including structure and recline hardware), kg. */
export const SEAT_WEIGHT_KG: ClassMap<number> = {
  ECONOMY: 11,
  PREMIUM_ECONOMY: 18,
  BUSINESS: 95,
  FIRST: 150,
};

/** One galley module including trolleys, ovens and standard catering load, kg. */
export const GALLEY_WEIGHT_KG = 180;

/** Satcom antenna, radome, server and cabling, kg. */
export const WIFI_HARDWARE_WEIGHT_KG = 120;

/** In-seat power wiring and outlets, kg per installed seat. */
export const IN_SEAT_POWER_WEIGHT_KG_PER_SEAT = 0.4;

/**
 * Extra fuel burned per flight hour for each kilogram of cabin weight above
 * the reference cabin (kg/hr per kg). About 3 % of carried weight per hour.
 */
export const FUEL_WEIGHT_PENALTY_FACTOR = 0.03;

/**
 * The AircraftType base burn assumes a reference cabin of this weight per
 * floor unit (a typical two-class cabin including lavatories and fittings).
 */
export const REFERENCE_CABIN_KG_PER_FLOOR_UNIT = 14;

/** Fuel burn never drops below this share of the type's base burn. */
export const MIN_FUEL_BURN_FRACTION = 0.5;

/* -------------------------------------------------------------------------- *
 * Comfort
 * -------------------------------------------------------------------------- */

/**
 * Comfort for one class is
 *   100 × clamp01(base + pitchWeight·pitch + densityWeight·density + amenityWeight·amenities)
 * and the four coefficients of each class sum to 1.
 */
export interface ComfortProfile {
  /** Comfort inherent to the product (seat width, recline, service). */
  readonly baseComfort: number;
  readonly pitchWeight: number;
  readonly densityWeight: number;
  readonly amenityWeight: number;
  /** Share of the amenity score earned by Wi-Fi; in-seat power earns the rest. */
  readonly wifiShare: number;
}

export const COMFORT_PROFILES: ClassMap<ComfortProfile> = {
  ECONOMY: { baseComfort: 0, pitchWeight: 0.55, densityWeight: 0.2, amenityWeight: 0.25, wifiShare: 0.5 },
  PREMIUM_ECONOMY: { baseComfort: 0.25, pitchWeight: 0.3, densityWeight: 0.15, amenityWeight: 0.3, wifiShare: 0.5 },
  BUSINESS: { baseComfort: 0.4, pitchWeight: 0.3, densityWeight: 0.1, amenityWeight: 0.2, wifiShare: 0.6 },
  FIRST: { baseComfort: 0.6, pitchWeight: 0.2, densityWeight: 0.05, amenityWeight: 0.15, wifiShare: 0.6 },
};

/** Pitch comfort (0–1) for classes whose pitch is fixed by the product, not the config. */
export const FIXED_PITCH_COMFORT = 0.5;

/** Cabin floor usage below this level has no crowding penalty. */
export const DENSITY_COMFORT_START_RATIO = 0.5;
/** Usage range over which the crowding penalty grows from 0 to its maximum. */
export const DENSITY_COMFORT_SPAN = 0.6;

/* -------------------------------------------------------------------------- *
 * Price tolerance
 * -------------------------------------------------------------------------- */

/**
 * Passengers expect `expectedComfort` for the class; the price they will pay
 * moves by `sensitivity` per 50 comfort points above or below it:
 *   multiplier = 1 + sensitivity × (comfort − expectedComfort) / 50
 */
export interface ToleranceProfile {
  readonly expectedComfort: number;
  readonly sensitivity: number;
}

export const TOLERANCE_PROFILES: ClassMap<ToleranceProfile> = {
  ECONOMY: { expectedComfort: 32, sensitivity: 0.3 },
  PREMIUM_ECONOMY: { expectedComfort: 45, sensitivity: 0.3 },
  BUSINESS: { expectedComfort: 60, sensitivity: 0.45 },
  FIRST: { expectedComfort: 70, sensitivity: 0.5 },
};

export const TOLERANCE_MIN_MULTIPLIER = 0.6;
export const TOLERANCE_MAX_MULTIPLIER = 1.6;

/* -------------------------------------------------------------------------- *
 * Validation thresholds
 * -------------------------------------------------------------------------- */

/** Floor usage at or above this ratio raises an informational notice. */
export const FLOOR_NEARLY_FULL_RATIO = 0.98;
/** Floor usage below this ratio raises an informational notice. */
export const FLOOR_UNDERUSED_RATIO = 0.5;
/** Seat count at or above this share of the certified limit raises a notice. */
export const CERTIFIED_NEAR_LIMIT_RATIO = 0.95;
/** Economy pitch below this (inches) is legal but flagged as very tight. */
export const TIGHT_ECONOMY_PITCH_INCHES = 29;

/** Cabins with this many seats or fewer need no galley. */
export const GALLEY_FREE_SEAT_LIMIT = 30;
/** Catering load one galley can serve. */
export const SERVICE_LOAD_PER_GALLEY = 110;
/** Catering load contributed by one seat of each class. */
export const SERVICE_LOAD_PER_SEAT: ClassMap<number> = {
  ECONOMY: 1,
  PREMIUM_ECONOMY: 1.5,
  BUSINESS: 3,
  FIRST: 5,
};
/** Galleys beyond the recommended count by more than this are called out. */
export const EXCESS_GALLEY_MARGIN = 2;

/** Tolerance when comparing floating-point floor unit totals. */
export const FLOOR_UNIT_EPSILON = 1e-9;
